import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, FileText, Send, TrendingUp, Eye, Download, ArrowLeft, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { SubscriptionGate } from "@/components/SubscriptionGate";

interface AnalyticsEvent {
  id: string;
  event_type: string;
  event_data: any;
  created_at: string;
  resume_id?: string;
  letter_id?: string;
}

const Analytics = () => {
  const navigate = useNavigate();
  const { subscription, hasFeature } = useSubscription();
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [stats, setStats] = useState({
    totalResumes: 0,
    totalLetters: 0,
    totalViews: 0,
    totalDownloads: 0,
    totalApplications: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasFeature('prioritySupport')) {
      // Analytics is Premium only
      return;
    }
    
    fetchAnalytics();
  }, [hasFeature]);

  const fetchAnalytics = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      // Fetch all events
      const { data: eventsData, error: eventsError } = await supabase
        .from("analytics_events")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);

      if (eventsError) throw eventsError;

      setEvents(eventsData || []);

      // Calculate stats
      const resumeCount = await supabase
        .from("resume_versions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      const letterCount = await supabase
        .from("letters")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      const views = eventsData?.filter(e => e.event_type === "view").length || 0;
      const downloads = eventsData?.filter(e => e.event_type === "download").length || 0;
      const applications = eventsData?.filter(e => e.event_type === "application_sent").length || 0;

      setStats({
        totalResumes: resumeCount.count || 0,
        totalLetters: letterCount.count || 0,
        totalViews: views,
        totalDownloads: downloads,
        totalApplications: applications,
      });

    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const trackEvent = async (eventType: string, data: any = {}) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("analytics_events").insert({
        user_id: user.id,
        event_type: eventType,
        event_data: data
      });

      toast.success("Event tracked");
      fetchAnalytics();
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  };

  if (!hasFeature('prioritySupport')) {
    return (
      <SubscriptionGate
        feature="Analytics Dashboard"
        requiredPlan="premium"
        description="Track your resume views, downloads, and application success with detailed analytics."
      />
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <Link to="/app" className="hidden md:block">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-4xl font-bold">Analytics Dashboard</h1>
              <p className="text-muted-foreground">Track your job search performance</p>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent>
            <div className="flex flex-col gap-4 mt-8">
              <Link to="/app">
                <Button variant="ghost" className="w-full justify-start">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <FileText className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Resumes</p>
              <p className="text-2xl font-bold">{stats.totalResumes}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <Send className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Cover Letters</p>
              <p className="text-2xl font-bold">{stats.totalLetters}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <Eye className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Views</p>
              <p className="text-2xl font-bold">{stats.totalViews}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <Download className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Downloads</p>
              <p className="text-2xl font-bold">{stats.totalDownloads}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Applications</p>
              <p className="text-2xl font-bold">{stats.totalApplications}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Track Activity</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => trackEvent("view")}>
            <Eye className="h-4 w-4 mr-2" /> Log Resume View
          </Button>
          <Button onClick={() => trackEvent("download")}>
            <Download className="h-4 w-4 mr-2" /> Log Download
          </Button>
          <Button onClick={() => trackEvent("application_sent")}>
            <Send className="h-4 w-4 mr-2" /> Log Application Sent
          </Button>
        </div>
      </Card>

      {/* Recent Events */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {events.length === 0 ? (
            <p className="text-muted-foreground">No activity yet. Start tracking your events above!</p>
          ) : (
            events.slice(0, 20).map((event) => (
              <div key={event.id} className="flex items-center justify-between border-b pb-2">
                <div>
                  <p className="font-medium capitalize">{event.event_type.replace(/_/g, " ")}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(event.created_at).toLocaleString()}
                  </p>
                </div>
                {event.event_data && Object.keys(event.event_data).length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {JSON.stringify(event.event_data)}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default Analytics;
